import {
  Box, Grid, Link,
} from '@mui/material';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { v4 as uuid } from 'uuid';
import HeaderMenuSettingsModal from '../shared/HeaderMenuSettingsModal';
import BlockData from '../../../../models/BlockData';
import BlockProps, { PreviewModeType } from '../../../../models/BlockProps';
import LinkData from '../../../../models/LinkData';
import BlockHeaderWithLogoData from './BlockHeaderWithLogoData';
import CloseButton from '../../../Buttons/Close';
import Utils from '../../../../utils/Utils';
import styles from './BlockHeaderWithLogo.module.css';
import LogoHeaderContactInfo from './components/LogoHeaderContactInfo';
import LogoHeaderButtons from './components/LogoHeaderButtons';
import HamburgerMenuButton from '../../../Buttons/HamburgerMenuButton';
import HeaderMenu from '../shared/HeaderMenu';
import BlockComponentImage from '../../../../models/BlockComponentValues/BlockComponentImage';
import ImageSettingsModal from '../../../Modals/ImageSettingsModal/ImageSettingsModal';
import CroppedImage from '../../../CroppedImage';
import { Size } from '../../../../models/BlockComponentSettings/ImageSettings';
import { ImageAspectRatio, ImageOrientation } from '../../BlockImageList/ImageSettings';
import useGetBackground from '../../../../hooks/UseGetBackground';
import BackGroundImageModal from '../../../Modals/ImageModal/BackGroundImageModal';
import useParallaxScript from '../../../../hooks/useParallaxScript';
import config from '../../../../config';
import { BlockJustify, BlockSeparator } from '../../../../models/BlockSettings';
import AutoCloseMenus from '../shared/AutoCloseMenus';
import ImageEnlargeModal from '../../../Images/ImageEnlargeModal';

export interface BlockHeaderWithLogoProps extends BlockProps {
  blockData: BlockData[],
  fontFamily?: string,
  backgroundColor?: string,
  previewMode?: PreviewModeType,
}

function BlockHeaderWithLogo({
  data,
  isEditing,
  isTemplate,
  editHandler,
  blockData,
  showImageModal,
  onCloseImageModal,
  mainColor,
  fontFamily,
  backgroundColor,
  previewMode,
}: BlockHeaderWithLogoProps) {
  const targetModalId = uuid();

  const [props, setProps] = useState<BlockHeaderWithLogoData>(data.data as BlockHeaderWithLogoData);

  useEffect(() => {
    setProps(data.data as BlockHeaderWithLogoData);
  }, [data.data]);

  const [open, setOpen] = useState(false);
  const hamburgerCheckbox = useRef(null);

  const [backgroundColorState, setBackgroundColorState] = useState<string | undefined>(backgroundColor || props.url?.solidColor);
  const [fontFamilyState, setFontFamilyState] = useState<string | undefined>(fontFamily);
  const [showMenu, setShowMenu] = useState(false);

  const handleOpen = useCallback(() => {
    if (isEditing && !isTemplate) {
      setOpen(true);
    }
  }, [isEditing, isTemplate]);
  const handleClose = () => setOpen(false);

  const handleMenuDelete = useCallback(() => {
    props.menus = undefined;
    editHandler(data.id, props);
  }, [data.id, editHandler, props]);

  const contactInfo = useMemo(() => (
    <LogoHeaderContactInfo
      contact={props.contact}
      contactInfo={props.contactInfo}
      isEditing={isEditing}
      isTemplate={isTemplate}
      onRemoveContact={() => {
        props.contact.value = undefined;
        editHandler(data.id, props);
      }}
      onSaveContact={(text) => {
        props.contact.value = text;
        editHandler(data.id, props);
      }}
      onRemoveContactInfo={() => {
        props.contactInfo.value = undefined;
        editHandler(data.id, props);
      }}
      onSaveContactInfo={(text) => {
        props.contactInfo.value = text;
        editHandler(data.id, props);
      }}
    />
  ), [data.id, editHandler, isEditing, isTemplate, props]);

  const buttons = useMemo(() => (
    <LogoHeaderButtons
      mainColor={mainColor}
      fontFamily={fontFamilyState}
      leftButton={props.leftButton}
      rightButton={props.rightButton}
      isEditing={isEditing}
      isTemplate={isTemplate}
      blockData={blockData}
      onRemoveLeftButton={() => {
        props.leftButton = undefined;
        editHandler(data.id, props);
      }}
      onSaveLeftButton={(item) => {
        props.leftButton = item;
        editHandler(data.id, props);
      }}
      onRemoveRightButton={() => {
        props.rightButton = undefined;
        editHandler(data.id, props);
      }}
      onSaveRightButton={(item) => {
        props.rightButton = item;
        editHandler(data.id, props);
      }}
    />
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [blockData, data.id, editHandler, isEditing, isTemplate, props]);

  const contactInfoAndButtons = useMemo(() => (
    <Grid
      id="logoButtonsInfo"
      className="logoButtonsInfo"
      container
      justifyContent="end"
      alignItems="center"
    >
      <Grid item xs="auto">
        {contactInfo}
      </Grid>

      {props.leftButton || props.rightButton ? (
        <Grid item xs="auto" sx={{ marginLeft: 1.5 }}>
          {buttons}
        </Grid>
      ) : null}
    </Grid>
  ), [buttons, contactInfo, props.leftButton, props.rightButton]);

  const hamburgerButton = useMemo(() => (
    <Grid
      id="logoButtonsHamburgerButton"
      className="logoButtonsHamburgerButton previewResponsiveShow"
      item
      xs="auto"
    >
      {isEditing || isTemplate ? (
        <HamburgerMenuButton />
      ) : (
        <label htmlFor={`hamburger-${data.id}`}>
          <HamburgerMenuButton />
        </label>
      )}
    </Grid>
  ), [isEditing, isTemplate]);

  const menuItems = useMemo<JSX.Element | []>(() => {
    if (props.menus && props.menus?.length > 0) {
      return (
        <Grid
          id="logoButtonsMenu"
          className="logoButtonsMenu"
          container
          sx={{ p: 1.5, paddingTop: 0 }}
        >
          <HeaderMenu
            showSeparators
            menus={props.menus}
            headerSettings={props.settings}
            isEditing={isEditing}
            handleMenuDelete={handleMenuDelete}
            handleOpen={handleOpen}
          />
        </Grid>
      );
    }

    return [];
  }, [props.menus, props.settings, isEditing, handleMenuDelete, handleOpen]);

  const hamburgerMenu = useMemo(() => (
    <Grid
      id="logoButtonsHamburgerMenuItems"
      className="logoButtonsHamburgerMenuItems previewResponsiveShow"
      container
      justifyContent="center"
      alignItems="center"
      direction="column"
      sx={{
        p: 1.5,
        backgroundColor: props.url?.solidColor ?? backgroundColor,
      }}
    >

      <Grid item xs={12} sx={{ width: '100%' }}>
        {contactInfo}
      </Grid>

      {props.menus && props.menus?.length > 0 ? (
        <Grid item xs={12} sx={{ width: '100%' }}>
          <HeaderMenu
            menus={props.menus}
            headerSettings={props.settings}
            isEditing={isEditing}
            direction="column"
            handleMenuDelete={handleMenuDelete}
            handleOpen={handleOpen}
          />
        </Grid>
      ) : null}

      <Grid item xs={12} sx={{ width: '100%' }}>
        {buttons}
      </Grid>

    </Grid>
  ), [buttons, contactInfo, handleMenuDelete, handleOpen, isEditing, props.menus, props.settings]);

  const [innerBoxSize, setInnerBoxSize] = useState<{ width: string, height: string }>({ width: '89px', height: '50px' });

  const calcInnerBoxSize = (frameSize: Size) => {
    let newWidth = 0;
    if (props.logo.settings.fixedAspect) {
      switch (props.logo.settings.orientation) {
        case ImageOrientation.Portrait:
        case ImageOrientation.PortraitOval:
          if (props.logo.settings.aspectRatio === ImageAspectRatio.SixteenByNine) {
            newWidth = (9 / 16) * 50;
          } else if (props.logo.settings.aspectRatio === ImageAspectRatio.FourByThree) {
            newWidth = (3 / 4) * 50;
          }
          break;
        case ImageOrientation.Square:
        case ImageOrientation.Circle:
          newWidth = 50;
          break;
        case ImageOrientation.Landscape:
        case ImageOrientation.LandscapeOval:
        default:
          if (props.logo.settings.aspectRatio === ImageAspectRatio.SixteenByNine) {
            newWidth = (16 / 9) * 50;
          } else if (props.logo.settings.aspectRatio === ImageAspectRatio.FourByThree) {
            newWidth = (4 / 3) * 50;
          }
          break;
      }
    } else {
      newWidth = (frameSize.width / frameSize.height) * 50;
    }

    const newHeight = '50px';
    setInnerBoxSize({ width: `${newWidth}px`, height: newHeight });
  };

  // effects
  useEffect(() => {
    if (!props.settings?.justify) {
      setProps({ ...props, settings: { ...props.settings, justify: BlockJustify.CENTER } });
    }

    if (!props.settings?.separator) {
      setProps({ ...props, settings: { ...props.settings, separator: BlockSeparator.NONE } });
    }
  }, [props]);

  useEffect(() => {
    if (props.logo) {
      calcInnerBoxSize(props.logo.settings.frameSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.logo]);

  useEffect(() => {
    if (isEditing && hamburgerCheckbox.current?.checked) {
      hamburgerCheckbox.current.checked = false;
    }
  }, [isEditing]);

  useEffect(() => {
    setFontFamilyState(fontFamily ?? 'Roboto');
  }, [fontFamily]);

  useEffect(() => {
    setBackgroundColorState(props.url?.solidColor ?? backgroundColor);
  }, [backgroundColor, props.url?.solidColor]);

  useEffect(() => {
    AutoCloseMenus();
  }, [data]);

  const elementUuid = uuid();
  useParallaxScript(props.url?.settings?.parallaxBool, elementUuid, config.backgroundImage.parallaxEffect);

  return (
    <>
      <Grid
        id={elementUuid}
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="space-between"
        className={`${styles.header} ${!props.isFree ? 'blockHeaderType' : ''}`}
        sx={{
          overflow: 'hidden',
          ...useGetBackground(
            backgroundColorState,
            props.url?.value,
            props.url?.settings?.scale,
            props.url?.settings?.scaleBool,
          ),
          containerType: `${previewMode === 'smartphone' ? 'inline-size' : 'inherit'}`,
          backgroundPosition: 'center 0px',
        }}
      >
        {(isEditing && !isTemplate) && (
          <BackGroundImageModal
            backgroundImage={props.url}
            defaultBackgroundColor={backgroundColor}
            isShowModal={showImageModal}
            onCloseModal={onCloseImageModal}
            handleOnConfirm={(url: BlockComponentImage) => {
              const tempBg = { ...props, url: { ...url } };
              setProps(tempBg);
              editHandler(data.id, tempBg);
            }}
          />
        )}

        <Grid container justifyContent="space-between" alignItems="center" sx={{ p: 1.5 }}>
          {props.logo ? (
            <Grid item xs={6} sx={Utils.hoverBorder(isEditing)}>
              <CloseButton
                show={isEditing}
                updateHandler={() => {
                  props.logo = undefined;
                  editHandler(data.id, props);
                }}
              />
              <Box
                className="innerBoxLogo"
                sx={{
                  maxHeight: '50px',
                  height: '50px',
                  width: 'auto',
                  overflow: 'hidden',
                }}
              >
                <ImageSettingsModal
                  isEditing={isEditing}
                  blockData={blockData}
                  hasLogo
                  image={props.logo}
                  disabled={isTemplate || !isEditing}
                  onConfirm={(newImage: BlockComponentImage) => {
                    calcInnerBoxSize(newImage.settings.frameSize);
                    props.logo = newImage;
                    editHandler(data.id, props);
                  }}
                >
                  <Box
                    sx={innerBoxSize}
                  >
                    {(!isEditing) ? (
                      <Link
                        style={{ cursor: props?.logo?.link?.linkData?.url ? 'pointer' : 'default' }}
                        target={props?.logo?.link?.linkData?.openType}
                        href={props?.logo?.link?.linkData?.url || undefined}
                        rel="noreferrer"
                      >
                        <CroppedImage
                          image={props.logo}
                          isEditing={isEditing}
                          settings={props.logo.settings}
                          targetModalId={targetModalId}
                        />
                      </Link>
                    ) : (
                      <CroppedImage
                        image={props.logo}
                        isEditing={isEditing}
                        settings={props.logo.settings}
                        targetModalId={targetModalId}
                      />
                    )}
                  </Box>
                </ImageSettingsModal>
              </Box>
            </Grid>
          ) : (
            <Grid item />
          )}

          <Grid item xs={6}>
            <Box
              id="logoButtonsWrapper"
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              {hamburgerButton}
              {contactInfoAndButtons}
            </Box>
          </Grid>

        </Grid>

        {props.menus && (
          <>
            {menuItems}
          </>
        )}

        <input
          style={{ display: 'none' }}
          type="checkbox"
          id={`hamburger-${data.id}`}
          data-target={`hamburger-menu-with-logo-${data.id}`}
          className="hamburger-checkbox"
        />
        <Box
          id={`hamburger-menu-with-logo-${data.id}`}
          className="responsive-menu block-header-menu-with-logo"
          sx={{ width: '100%' }}
          style={{
            pointerEvents: (isEditing || isTemplate) ? 'none' : 'auto',
          }}
        >
          {hamburgerMenu}
        </Box>

        <HeaderMenuSettingsModal
          hasJustifyOptions
          hasExtraJustifyments={false}
          menuItems={props.menus ?? []}
          settings={props.settings}
          open={open}
          closeHandler={(newItems?: LinkData[]) => {
            if (newItems) {
              props.menus = newItems;
              editHandler(data.id, props);
            }
            handleClose();
          }}
          updateHandler={(settings) => {
            props.settings = settings;
          }}
          blockData={blockData}
        />

      </Grid>
      {!isEditing && (
        <Box
          sx={{
            backgroundColor: `${props.url?.solidColor ?? backgroundColor} !important`,
          }}
        />
      )}

      <ImageEnlargeModal
        images={[props.logo]}
        targetModalId={targetModalId}
      />
    </>
  );
}

export default BlockHeaderWithLogo;
